import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import InstallAppButton from "./InstallAppButton";
import { Button } from "./ui/button";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const setLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
  };

  return (
    <header className="border-b-2 border-[#1C1917] bg-[#FDFBF7] sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-8 h-8 bg-[#E5484D] border-2 border-[#1C1917] grid place-items-center text-white text-[11px] font-black tracking-tighter shadow-[2px_2px_0_0_#1C1917]">
            DC
          </div>
          <span className="display text-xl">DocuConvert</span>
          {offline && (
            <span className="hidden sm:inline-flex items-center gap-1.5 border-2 border-[#1C1917] bg-[#FFC53D] px-2 py-0.5 text-[10px] font-black tracking-wider uppercase" data-testid="header-offline">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E5484D] inline-block" />
              Offline
            </span>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <NavLink
            to="/tools/convert"
            className={({ isActive }) =>
              `transition-colors ${isActive ? "text-black font-medium" : "text-zinc-500 hover:text-black"}`
            }
            data-testid="nav-convert"
          >
            {t("nav_convert")}
          </NavLink>
          <NavLink
            to="/tools/pdf"
            className={({ isActive }) =>
              `transition-colors ${isActive ? "text-black font-medium" : "text-zinc-500 hover:text-black"}`
            }
            data-testid="nav-pdf"
          >
            {t("nav_pdf")}
          </NavLink>
          {user && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-black font-medium" : "text-zinc-500 hover:text-black"}`
              }
              data-testid="nav-dashboard"
            >
              {t("nav_dashboard")}
            </NavLink>
          )}
          {user && (
            <NavLink
              to="/billing"
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-black font-medium" : "text-zinc-500 hover:text-black"}`
              }
              data-testid="nav-billing"
            >
              {t("nav_billing")}
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-[#E5484D] font-bold" : "text-[#E5484D] hover:text-[#CE2C31]"}`
              }
              data-testid="nav-admin"
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <InstallAppButton variant="header" />
          <div className="hidden sm:flex items-center border-2 border-[#1C1917] overflow-hidden text-xs font-bold" data-testid="lang-switcher">
            <button
              onClick={() => setLang("es")}
              className={`px-2.5 py-1 transition-colors ${i18n.language === "es" ? "bg-[#1C1917] text-white" : "text-[#1C1917] hover:bg-[#FFC53D]"}`}
              data-testid="lang-es"
            >
              ES
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 transition-colors ${i18n.language === "en" ? "bg-[#1C1917] text-white" : "text-[#1C1917] hover:bg-[#FFC53D]"}`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          {user ? (
            <>
              <span className="hidden sm:inline text-xs text-[#57534E] max-w-[180px] truncate">
                {user.email}
              </span>
              <Button
                variant="outline"
                className="rounded-none border-2 border-[#1C1917] bg-white hover:bg-[#FFC53D] text-[#1C1917] font-bold shadow-[2px_2px_0_0_#1C1917] hover:shadow-[3px_3px_0_0_#1C1917]"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                data-testid="btn-logout"
              >
                {t("cta_logout")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                className="rounded-none hidden sm:inline-flex hover:bg-[#FFC53D] font-bold text-[#1C1917]"
                onClick={() => navigate("/login")}
                data-testid="btn-login"
              >
                {t("cta_login")}
              </Button>
              <Button
                className="rounded-none border-2 border-[#1C1917] bg-[#E5484D] text-white hover:bg-[#CE2C31] font-bold shadow-[3px_3px_0_0_#1C1917] hover:shadow-[4px_4px_0_0_#1C1917] hover:-translate-y-[1px] active:translate-y-[2px] active:shadow-[1px_1px_0_0_#1C1917] transition-all"
                onClick={() => navigate("/register")}
                data-testid="btn-register"
              >
                {t("cta_register")}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
